import { Button, Dialog, Modal, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { Input } from 'client/common/form-inputs';
import translations from 'src/internationalisation/translation-map.json';
import {
  addRigOperationTemplate,
  updateRigOperationTemplateItem,
} from 'store/entities/company-settings/company-settings';
import { useForm } from 'react-hook-form';
import { getResolver } from 'src/validation/resolver';
import { rigOperationTemplateSchemaValidator } from 'schemas/ajv-validators';
import { IRigOperationTemplate } from 'src/common/interfaces/rig-operations-template.interfaces';
import { convertToKebabCase } from 'client/common/utils/string-utils';

interface RigOperationRenameModalProps {
  visible: boolean;
  rename: boolean;
}
interface RigOperationTemplateRenameModalProps extends PropsFromRedux {
  activeRigOperationTemplate: IRigOperationTemplate | undefined;
  setRigOperationRenameModalVisible: (props: RigOperationRenameModalProps) => void;
  rename: boolean;
}

const RigOperationTemplateRenameModal = ({
  setRigOperationRenameModalVisible,
  activeRigOperationTemplate,
  updateRigOperationTemplateItem,
  addRigOperationTemplate,
  rename,
}: RigOperationTemplateRenameModalProps) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      rigOperationTemplateId: activeRigOperationTemplate?.rigOperationTemplateId || '',
      name: activeRigOperationTemplate?.name || '',
      activities: activeRigOperationTemplate?.activities || [],
    },
    resolver: getResolver(rigOperationTemplateSchemaValidator),
    mode: 'onChange',
  });
  const onClose = () => setRigOperationRenameModalVisible({ visible: false, rename: false });
  const onSubmit = handleSubmit((data) => {
    if (rename) {
      updateRigOperationTemplateItem({ ...data });
    } else {
      addRigOperationTemplate({ ...data });
    }
    setRigOperationRenameModalVisible({ visible: false, rename: false });
  });

  return (
    <Modal centered visible>
      <Dialog
        dialog={{
          heading: rename ? t(translations.renameTemplate) : t(translations.nameTemplate),
          content: (
            <form>
              <Input
                name="name"
                width={300}
                control={control}
                errors={errors}
                testId={`rig-operation-template-add-rename-overlay-input-${convertToKebabCase(t(translations.name))}`}
              />
            </form>
          ),
          footer: (
            <>
              <Button
                label={rename ? t(translations.rename) : t(translations.add)}
                colored
                onClick={onSubmit}
                testId={`rig-operation-template-add-rename-overlay-button-${convertToKebabCase(rename ? t(translations.rename) : t(translations.add))}`}
              />
              <Button
                label={t(translations.cancel)}
                onClick={onClose}
                testId={`rig-operation-template-add-rename-overlay-button-${convertToKebabCase(t(translations.cancel))}`}
              />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};

const mapDispatchToProps = { addRigOperationTemplate, updateRigOperationTemplateItem };

const connector = connect(null, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(RigOperationTemplateRenameModal), {
  style: { position: 'absolute', bottom: 0, zIndex: 1000 },
});

export { Container as RigOperationTemplateRenameModal };
